import {
    Box, capitalize,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import {GridColDef, GridRowId} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    FieldSort,
    useDataRequest,
} from "../../data";
import {useT} from "../../../i18n";
import DataTable from "../../data/DataTable";
import {useGetApi} from "../../../client";
import EditIcon from "@mui/icons-material/Edit";
import HelpIcon from "@mui/icons-material/Help";
import EntferneAnbauteilFahrzeugZuweisung from "../../../pages/fuhrpark/fahrzeug/EntferneAnbauteilFahrzeugZuweisung";
import ZugewieseneAnbauteilKostenDataTable from "./ZugewieseneAnbauteilKostenDataTable";
import NeuerKostensatzAnbauteilZuFahrzeug from "../../../pages/fuhrpark/fahrzeug/NeuerKostensatzAnbauteilZuFahrzeug";
import DeleteZugewiesenesAnbauteilButton from "./DeleteZugewiesenesAnbauteilButton";
import Guard from "../../Guard";
import useCustomFilters from "../../data/useCustomFilters";
import SearchField from "../../SearchField";

export type ZugewiesenesAnbauteilFilter = {
    fahrzeugId: string;
    search?: string;
    filtered?: string;
};

export type ZugewiesenesAnbauteil = {
    id: string;
    anbauteilId: string;
    kostenStunde: number;
};

export type Props = Omit<DataRequest<ZugewiesenesAnbauteilFilter>, "filter"> &
    Partial<Pick<DataRequest<ZugewiesenesAnbauteilFilter>, "filter">>;

const tableName: string = "anbauteil-fahrzeug"

export default function ZugewieseneAnbauteileDataTable({...input}: Props) {
    const {id: fahrzeugId = ""} = useParams<{
        id: string;
    }>();
    const request = useDataRequest<ZugewiesenesAnbauteilFilter>({
        filter: {fahrzeugId: fahrzeugId},
        ...input,
    });
    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <AnbauteilResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<ZugewiesenesAnbauteilFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<ZugewiesenesAnbauteilFilter>({
        tableName: tableName,
        defaultFilter: {
            fahrzeugId: request.filter.fahrzeugId,
            search: "",
        }
    });
    useEffect(() => {
        setFilter({
            fahrzeugId: currentCustomFilter.fahrzeugId,
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            <SearchField
                value={currentCustomFilter.search}
                onChange={(val) => setCustomField("search", val)}
            />
        </Box>
    );
}

function AnbauteilResults({
                              request,
                              setFilteredState,
                          }: {
    request: DataRequestState<ZugewiesenesAnbauteilFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<ZugewiesenesAnbauteilFilter>;
    setFilteredState: (value: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [openAssign, setOpenAssign] = useState(false);
    const [openAssignedCosts, setOpenAssignedCosts] = useState(false);
    const [zugewiesenesAnbauteilId, setZugewiesenesAnbauteilId] =
        useState<string>("");
    const [anbauteilId, setAnbauteilId] = useState<string>("");
    const getApi = useGetApi();
    const {t: translation} = useT("fuhrpark");

    const handleClickOpenAssign = (
        zugewiesenesAnbauteilId: GridRowId,
        anbauteilRowId: GridRowId
    ) => {
        setOpenAssign(true);
        setZugewiesenesAnbauteilId(zugewiesenesAnbauteilId.toString());
        setAnbauteilId(anbauteilRowId.toString());
    };

    const handleClickOpen = (
        zugewiesenesAnbauteilId: GridRowId,
        anbauteilRowId: GridRowId
    ) => {
        setOpen(true);
        setZugewiesenesAnbauteilId(zugewiesenesAnbauteilId.toString());
        setAnbauteilId(anbauteilRowId.toString());
    };

    const handleClickOpenAssignedCosts = (
        zugewiesenesAnbauteilId: GridRowId,
        anbauteilRowId: GridRowId
    ) => {
        setOpenAssignedCosts(true);
        setZugewiesenesAnbauteilId(zugewiesenesAnbauteilId.toString());
        setAnbauteilId(anbauteilRowId.toString());
    };

    const handleClose = () => {
        setOpen(false);
        setOpenAssign(false);
        setOpenAssignedCosts(false);
    };

    const ladeZugewieseneAnbauteile: DataLoader<
        ZugewiesenesAnbauteilFilter,
        ZugewiesenesAnbauteil
    > = useCallback(
        async (params) =>
            (await getApi()).fahrzeuge.sucheZugewieseneFahrzeugAnbauteile({
                ...params,
            }),
        [getApi]
    );

    const defaultSortKostenHistorie: FieldSort[] = [
        {field: "gueltigVon", direction: "desc"},
    ];

    return (
        <>
            <DataTable
                columns={useColumns(
                    handleClickOpen,
                    handleClickOpenAssign,
                    handleClickOpenAssignedCosts
                )}
                request={request}
                queryKey={[tableName]}
                name={tableName}
                loadData={ladeZugewieseneAnbauteile}
                setFiltered={setFilteredState}
            />
            <Dialog
                title={capitalize(translation("edit-attachment-part-assigned"))}
                open={open}
                onClose={handleClose}
            >
                <DialogContent>
                    <NeuerKostensatzAnbauteilZuFahrzeug
                        zugewiesenesAnbauteilId={zugewiesenesAnbauteilId}
                        anbauteilId={anbauteilId}
                        onClose={handleClose}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                title={capitalize(translation("remove-attachment-part-assigned"))}
                open={openAssign}
                onClose={handleClose}
            >
                <DialogContent>
                    <EntferneAnbauteilFahrzeugZuweisung
                        zugewiesenesAnbauteilId={zugewiesenesAnbauteilId}
                        anbauteilId={anbauteilId}
                        onClose={handleClose}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                title={capitalize(translation("assigned-costs-header"))}
                open={openAssignedCosts}
                onClose={handleClose}
                maxWidth={"lg"}
                fullWidth={true}
            >
                <DialogContent>
                    <ZugewieseneAnbauteilKostenDataTable
                        zugewiesenesAnbauteilId={zugewiesenesAnbauteilId}
                        anbauteilId={anbauteilId}
                        sort={defaultSortKostenHistorie}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}

function useColumns(
    handleDialogOpen: (
        zugewiesenesAnbauteilId: GridRowId,
        anbauteilRowId: GridRowId
    ) => void,
    handleAssignDialogOpen: (
        zugewiesenesAnbauteilId: GridRowId,
        anbauteilRowId: GridRowId
    ) => void,
    handleAssignedCostsDialogOpen: (
        zugewiesenesAnbauteilId: GridRowId,
        anbauteilRowId: GridRowId
    ) => void
): Array<GridColDef<ZugewiesenesAnbauteil>> {
    const {t: translation} = useT("fuhrpark");

    return useMemo(
        () => [
            {
                field: "geraeteBezeichnung",
                headerName: capitalize(translation("attachment-part-title")),
                flex: 1,
            },
            {
                field: "kostenStunde",
                headerName: capitalize(translation("assign-attachment-part-cost-hour")),
                flex: 1,
                headerAlign: "right",
                align: "right",
            },
            {
                field: "gueltigVon",
                headerName: capitalize(translation("assign-attachment-part-valid-from")),
                flex: 1,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleString();
                    }
                    return "";
                },
            },
            {
                field: "editAssignedAnbauteil",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 60,
                renderCell: (params) => (
                    <>
                        <Guard permission={"fuhrpark:edit"}>
                            <IconButton
                                onClick={() =>
                                    handleDialogOpen(params.row.id, params.row.anbauteilId)
                                }
                            >
                                <EditIcon/>
                            </IconButton>
                        </Guard>
                    </>
                ),
            },
            {
                field: "infoAssignedAnbauteilKosten",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 60,
                renderCell: (params) => (
                    <>
                        <IconButton
                            onClick={() =>
                                handleAssignedCostsDialogOpen(
                                    params.row.id,
                                    params.row.anbauteilId
                                )
                            }
                        >
                            <HelpIcon/>
                        </IconButton>
                    </>
                ),
            },
            {
                field: "removeAssignmentAnbauteil",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 60,
                renderCell: (params) => (
                    <>
                        <Guard permission={"fuhrpark:delete"}>
                            <DeleteZugewiesenesAnbauteilButton id={params.row.id}/>
                        </Guard>
                    </>
                ),
            },
        ],
        [translation, handleDialogOpen, handleAssignedCostsDialogOpen]
    );
}
